import React, { useEffect, useRef, useState } from "react";
import SpeakerModal from "../Speakers/SpeakerModal";
import { Link } from "react-router-dom";
import "../../styles/Speaker.scss";

interface SpeakerItem {
  id: number | string;
  name: string;
  description: string;
  image: string;
}

interface SpeakerJson {
  speakers: SpeakerItem[];
}

const Speaker: React.FC = () => {
  const [speakers, setSpeakers] = useState<SpeakerItem[]>([]);
  const [visibleIndex, setVisibleIndex] = useState<number[]>([]);
  const [openedIndex, setOpenedIndex] = useState<number[]>([]);
  const [selectedSpeaker, setSelectedSpeaker] =
    useState<SpeakerItem | null>(null);

  const sliderRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const rangeRef = useRef<HTMLInputElement | null>(null);

  const [maxScroll, setMaxScroll] = useState(0);
  const [rangeValue, setRangeValue] = useState(0);

  const coverColors = [
    "#ffab53",
    "#56f270",
    "#64c6f8",
    "#d9c1ff",
    "#ffc1c1",
    "#fffec1",
    "#9257EF",
  ];

  useEffect(() => {
    fetch("/resources/speaker.json")
      .then((res) => res.json())
      .then((data: SpeakerJson) => {
        setSpeakers(data.speakers || []);
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (!speakers.length) return;

    const section = containerRef.current;
    if (!section) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            speakers.forEach((_, idx) => {
              setTimeout(() => {
                setVisibleIndex((prev) => [...prev, idx]);
                setOpenedIndex((prev) => [...prev, idx]);
              }, idx * 400);
            });
            observer.disconnect();
          }
        });
      },
      { threshold: 0.3 }
    );

    observer.observe(section);
    return () => observer.disconnect();
  }, [speakers]);

  useEffect(() => {
    const container = containerRef.current;
    const range = rangeRef.current;
    if (!container || !range) return;

    const updateMax = () => {
      const max = Math.max(0, container.scrollWidth - container.clientWidth);
      setMaxScroll(max);
      range.max = String(max);

      const left = Math.round(container.scrollLeft);
      setRangeValue(left);
      range.value = String(left);
    };

    updateMax();
    const ro = new ResizeObserver(updateMax);
    ro.observe(container);

    const onContainerScroll = () => {
      const left = Math.round(container.scrollLeft);
      setRangeValue(left);
      range.value = String(left);
    };

    container.addEventListener("scroll", onContainerScroll, { passive: true });

    const onRangeInput = (e: Event) => {
      const val = Number((e.target as HTMLInputElement).value);
      container.scrollTo({ left: val, behavior: "auto" });
      setRangeValue(val);
    };

    range.addEventListener("input", onRangeInput);

    return () => {
      ro.disconnect();
      container.removeEventListener("scroll", onContainerScroll);
      range.removeEventListener("input", onRangeInput);
    };
  }, []);

  useEffect(() => {
    const range = rangeRef.current;
    if (!range) return;

    const onPointerDown = () => (document.body.style.userSelect = "none");
    const onPointerUp = () => (document.body.style.userSelect = "");

    range.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("pointerup", onPointerUp);

    return () => {
      range.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointerup", onPointerUp);
      document.body.style.userSelect = "";
    };
  }, []);

  useEffect(() => {
    const cursor = document.getElementById("speakerCursor");
    const container = containerRef.current;
    if (!cursor || !container) return;

    const moveCursor = (e: MouseEvent) => {
      cursor.style.transform = `translate(${e.clientX}px, ${e.clientY}px)`;
    };

    const showCursor = () => cursor.classList.add("active");
    const hideCursor = () => cursor.classList.remove("active");

    container.addEventListener("mousemove", moveCursor);
    container.addEventListener("mouseenter", showCursor);
    container.addEventListener("mouseleave", hideCursor);

    return () => {
      container.removeEventListener("mousemove", moveCursor);
      container.removeEventListener("mouseenter", showCursor);
      container.removeEventListener("mouseleave", hideCursor);
    };
  }, []);

//   useEffect(() => {
//   const cursor = document.getElementById("speakerCursor");
//   const container = containerRef.current;

//   if (!cursor || !container) return;

//   const moveCursor = (e: MouseEvent) => {
//     cursor.style.left = `${e.clientX}px`;
//     cursor.style.top = `${e.clientY}px`;
//   };

//   const showCursor = () => cursor.classList.add("active");
//   const hideCursor = () => cursor.classList.remove("active");

//   container.addEventListener("mousemove", moveCursor);
//   container.addEventListener("mouseenter", showCursor);
//   container.addEventListener("mouseleave", hideCursor);

//   return () => {
//     container.removeEventListener("mousemove", moveCursor);
//     container.removeEventListener("mouseenter", showCursor);
//     container.removeEventListener("mouseleave", hideCursor);
//   };
// }, []);


  const onCardClick = (speaker: SpeakerItem) => {
    setSelectedSpeaker(speaker);
  };

  if (!speakers.length) return null;

  return (
    <section className="speaker-section">
      <div className="header">
        <div className="header-left">
          <h2>Our Speakers</h2>
          <p>Visionaries. Trailblazers. Industry Game-Changers.</p>
        </div>

        <div className="header-right">
          <Link to="/speakers" className="see-more-btn">
            See more
          </Link>
        </div>
      </div>

      <div className="slider-container" ref={containerRef}>
        <div className="slider" ref={sliderRef}>
          {speakers.map((speaker, index) => (
            <div
              key={speaker.id}
              className={`speaker-card ${
                visibleIndex.includes(index) ? "show" : ""
              }`}
              style={{
                backgroundImage: `url(${speaker.image})`,
                transitionDelay: `${index * 0.2}s`,
              }}
              onClick={() => onCardClick(speaker)}
            >
              <div
                className={`speaker-cover ${
                  openedIndex.includes(index) ? "opened" : ""
                }`}
                style={{
                  backgroundColor:
                    coverColors[index % coverColors.length],
                }}
              />

              <div className="panel">
                <div className="name">{speaker.name}</div>
                <div className="description">{speaker.description}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="scroll-indicator">
        <div className="scroll-track">
          <div
            className="scroll-thumb"
            style={{
              width: maxScroll === 0 ? "100%" : "50%",
              transform:
                maxScroll === 0
                  ? "translateX(0)"
                  : `translateX(${
                      (rangeValue / Math.max(1, maxScroll)) * 100
                    }%)`,
            }}
          />
        </div>
        <input
          ref={rangeRef}
          className="scroll-range-center"
          type="range"
          defaultValue={0}
          aria-label="Speakers scroll"
        />
      </div>

      {selectedSpeaker && (
        <SpeakerModal
          speaker={selectedSpeaker}
          onClose={() => setSelectedSpeaker(null)}
        />
      )}

      <div className="speaker-cursor" id="speakerCursor" />
    </section>
  );
};

export default Speaker;
